import { EmptyState } from 'module/ui/components/atoms/empty-state';
import { faFileExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Ai } from '@tiptap-pro/extension-ai-advanced';
import DragHandle from '@tiptap-pro/extension-drag-handle';
// import { DragHandle } from '@tiptap-pro/extension-drag-handle';
import { useMemo, useState } from 'react';
// import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import './styles.css';
import '../../../components/organisms/document-editor/extensions/section-menu/styles.css';
import { InsertOnlyGuard } from 'components/organisms/document-editor/extensions/typing-disabled/extension';
import { AskAiCommand } from './extensions/ask-ai/commands/command';
import { ElementInspectorExtension } from './extensions/ask-ai-inspector/extension';
import { AskAiSelectionHighlightDecoration } from './extensions/aski-ai-selection-highlight/mark';
import { AutoSaveExtension } from './extensions/auto-save/extension';
import { RlCitation } from './extensions/citation/extension';
import { CommandsPicker } from './extensions/command-picker/extension';
import { CommandPickerWithTrigger } from './extensions/command-picker-with-trigger/extension';
import { RlGroupSection } from './extensions/custom-group-section/extension';
import { RlCustomSection } from './extensions/custom-section/extension';
import { DistractionFreeExtension } from './extensions/distraction-free/extension';
import { EndOfDocumentExtension } from './extensions/end-of-document/extension';
import { FeedbackWithTrigger } from './extensions/feedback/extension';
import { RlGroupedSectionTemp } from './extensions/grouped-section-temp/extension';
import { Highlight } from './extensions/highlight/mark';
import { NodeIdGlobalExtension } from './extensions/node-id/extension';
import { RlPlaceholderConfig } from './extensions/placeholder-config/extension';
import { SectionMenu } from './extensions/section-menu';
import { AutoLastColumnTable } from './extensions/table/table-column/extension';
import { VersionHistoryTriggerExtension } from './extensions/version-history-trigger';
import { FloatingToolbar as DefaultFloatingToolbar } from './toolbar/default-toolbar';
import {
  defaultExtensionConfig,
  defaultDocumentFloatingToolbarConfig,
  DocumentEditorProps,
} from './types';
import { isCustomToolbarConfig } from './utils/common';
import { RichTextEditor } from '../../../module/ui/components/atoms/rich-text-editor';
import {
  EditorFloatingToolbarConfig,
  RichTextEditorProps,
  RichTextEditorValueType,
} from '../../../module/ui/components/atoms/rich-text-editor/types';

type StyledEditorProps = {
  $hasFloatingContentOutsideOfEditor: boolean;
  $disableGapCursor?: boolean;
  $readonly?: boolean;
};
type EditorType = <ValueType extends RichTextEditorValueType>(
  props: RichTextEditorProps<ValueType> & StyledEditorProps,
) => React.JSX.Element;

const EditorFallbackWrapper = styled.div`
  padding: var(--spacing-09);
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;

const ReloadWrapper = styled.div`
  cursor: pointer;
  color: var(--text-secondary);
  text-decoration-line: underline;
  text-decoration-style: solid;
`;

const Editor = styled(RichTextEditor)<StyledEditorProps>`
  display: flex;
  flex-grow: 1;
  flex-direction: row;

  .ProseMirror-gapcursor {
    display: ${({ $disableGapCursor }) =>
      $disableGapCursor ? 'none' : 'block'};
  }

  > .rte-editor {
    height: auto;
    flex-grow: 1;
    max-height: 100%;
    overflow-y: auto;
    ${({ $hasFloatingContentOutsideOfEditor }) =>
      $hasFloatingContentOutsideOfEditor &&
      css`
        padding: var(--spacing-05);
        margin: calc(var(--spacing-05) * -1);
      `};

    .custom-section:not(:first-child):not([data-content-state='prompt']) {
      margin-top: 2rem;
    }

    .MULTI_QUESTIONS {
      &.GENERATED {
        padding: var(--spacing-05)
          ${({ $readonly }) =>
            $readonly ? 'var(--spacing-08)' : 'var(--spacing-05)'}
          var(--spacing-05) 0 !important;

        .custom-group-section-generated {
          h2 {
            margin-bottom: 0.75rem !important;
          }
          > p {
            margin: 0 !important;
            padding: 0 !important;
            line-height: 0 !important;
          }
          .custom-section-content.GENERATED
            .question-answer-item-generated
            > [data-node-view-wrapper] {
            > p {
              margin: 0 !important;
              padding: 0 !important;
              line-height: 0 !important;
            }
          }
        }
      }
      .custom-section {
        padding: 0 !important;
        margin: 0 !important;
        border: none !important;

        .tableWrapper {
          margin: 0 !important;
          padding: 0 !important;
          border: 0 !important;
        }

        table {
          margin: 0 !important;
          padding: 0 !important;
          border: 0 !important;

          td {
            border-bottom: none !important;
          }

          tr {
            border-left: none !important;
          }

          tr td:first-child {
            width: 8%;

            .table-cell-view,
            .table-cell-wrapper {
              padding: 0;
              margin: 0;
              justify-content: center;
              align-items: center;
            }
          }

          .table-cell-view,
          .table-cell-wrapper {
            padding-left: 0;
          }

          tr:nth-child(odd) {
            border-right: 0;
            color: var(--text-primary);
            font-size: var(--font-size-base);
            font-weight: var(--font-weight-bold);
            padding: 0;
          }
        }

        &:last-child table {
          border-bottom: 1px solid var(--scarlet-gray-30) !important;
        }
      }
    }
    .custom-section {
      position: relative;
      cursor: grab;
      display: flex;
      justify-content: center;
      &.is-editor-empty::before {
        content: none;
      }

      &[data-content-state='generated'] {
        padding: 0 0 var(--spacing-05) var(--spacing-05);
        padding-right: ${({ $readonly }) =>
          $readonly ? 'var(--spacing-08)' : '0'};
        border: 1px solid transparent;
        border-radius: var(--border-radius-small);

        &:hover {
          border: 1px solid var(--divider-default);
        }
      }

      .custom-section-content {
        width: 100%;
        outline: none;

        &.MULTI_QUESTION {
          tr td:first-child {
            width: 5%;
          }

          tr,
          table {
            border-left: 0;
          }

          td {
            .table-cell-view,
            .table-cell-wrapper {
              padding-left: 0;
            }

            .node-RL_PLACEHOLDER_CONFIG {
              color: var(--text-secondary);
              font-weight: var(--font-weight-regular);
            }
          }

          tr:nth-child(odd) {
            border-right: 0;
            color: var(--text-primary);
            font-size: var(--font-size-base);
            font-weight: var(--font-weight-bold);
            padding: 0;
          }

          tr:nth-child(even) {
            border-bottom: 2px solid var(--divider-default);
          }
        }
      }

      &:active {
        cursor: grabbing;
      }
    }

    .custom-drag-handle {
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--background-default);
      border-radius: 4px;
      cursor: grab;
      opacity: 0;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 1;
        background: var(--background-hover);
      }

      &:active {
        cursor: grabbing;
      }
    }
  }
` as EditorType;

const RichTextEditorComponent = <ValueType extends RichTextEditorValueType>({
  value,
  feedbackPayload,
  placeholder,
  onChange,
  onInitialize,
  className,
  floatingToolbarConfig = defaultDocumentFloatingToolbarConfig,
  extensionConfig: extensionConfigProp = defaultExtensionConfig,
  readonly = false,
  canDragAndDrop = false,
  isInsertOnlyMode = false,
  extensions: extensionsProp = [],
  isChatOpen,
  documentTitle,
  pageType: pageTypeProp,
}: DocumentEditorProps<ValueType>) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isDragging, setIsDragging] = useState(false);

  const extensionConfig = useMemo(() => {
    return {
      ...defaultExtensionConfig,
      ...extensionConfigProp,
    };
  }, [extensionConfigProp]);

  const extensions = useMemo(
    () => [
      InsertOnlyGuard.configure({ isInsertOnlyMode: isInsertOnlyMode }),
      ...(canDragAndDrop
        ? [
            DragHandle.configure({
              tippyOptions: {
                placement: 'left',
              },
              // Configure DragHandle to work with custom sections
              render: () => {
                const element = document.createElement('div');

                element.innerHTML = `
                <div style="
                  width: 24px;
                  height: 24px;
                  background: var(--background-default, #fff);
                  border-radius: 4px;
                  cursor: grab;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                  opacity: 0.7;
                  transition: opacity 0.2s ease;
                ">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="9" cy="5" r="1"/>
                    <circle cx="9" cy="12" r="1"/>
                    <circle cx="9" cy="19" r="1"/>
                    <circle cx="15" cy="5" r="1"/>
                    <circle cx="15" cy="12" r="1"/>
                    <circle cx="15" cy="19" r="1"/>
                  </svg>
                </div>
              `;
                element.firstElementChild?.classList.add('tahseen');

                return element.firstElementChild as HTMLElement;
              },
              onNodeChange: ({ node }) => {
                const element = document.querySelector(
                  '.tahseen',
                ) as HTMLElement | null;

                if (node && node.type.name === 'RL_CUSTOM_SECTION') {
                  // Enable drag handle for custom sections
                  element?.style?.setProperty('display', 'flex', 'important');

                  return true;
                } else {
                  element?.style?.setProperty('display', 'none', 'important');

                  return false;
                }
              },
            }),
          ]
        : []),
      Highlight,
      Ai,
      RlPlaceholderConfig,
      AutoLastColumnTable.configure({
        showEditing: isInsertOnlyMode || !readonly,
      }),
      FeedbackWithTrigger.configure({
        visible:
          !extensionConfig.elementInspectorConfig.enabled &&
          extensionConfig.likeDislikeConfig.enabled,
        feedbackPayload,
      }),
      RlCitation.configure(extensionConfig.citationConfig),
      RlGroupedSectionTemp,
      ...(extensionConfig.elementInspectorConfig.enabled
        ? [
            ElementInspectorExtension.configure(
              extensionConfig.elementInspectorConfig,
            ),
          ]
        : []),
      RlCustomSection.configure(extensionConfig.customSectionConfig),
      RlGroupSection.configure(extensionConfig.groupSectionConfig),
      ...(extensionConfig.commandPickerWithTriggerConfig.enabled
        ? [
            CommandPickerWithTrigger.configure(
              extensionConfig.commandPickerWithTriggerConfig,
            ),
          ]
        : []),
      ...(extensionConfig.commandsPickerConfig.enabled
        ? [CommandsPicker.configure(extensionConfig.commandsPickerConfig)]
        : []),
      DistractionFreeExtension.configure(extensionConfig.distractionFreeConfig),
      ...(extensionConfig.endOfDocumentConfig.enabled
        ? [EndOfDocumentExtension]
        : []),
      ...(extensionConfig.askAiConfig.enabled
        ? [
            AskAiSelectionHighlightDecoration,
            AskAiCommand.configure({
              processAskAiRequest:
                extensionConfig.askAiConfig.processAskAiRequest,
              contentReplacementOptions:
                extensionConfig.askAiConfig.contentReplacementOptions,
            }),
          ]
        : []),
      ...(extensionConfig.autoSaveConfig.enabled
        ? [AutoSaveExtension.configure(extensionConfig.autoSaveConfig)]
        : []),
      ...(extensionConfig.sectionMenuConfig.enabled
        ? [SectionMenu.configure(extensionConfig.sectionMenuConfig)]
        : []),
      ...(extensionConfig.versionHistoryConfig.enabled
        ? [
            VersionHistoryTriggerExtension.configure(
              extensionConfig.versionHistoryConfig,
            ),
          ]
        : []),
      NodeIdGlobalExtension,
      ...extensionsProp,
    ],
    [
      extensionConfig,
      extensionsProp,
      feedbackPayload,
      isInsertOnlyMode,
      canDragAndDrop,
      readonly,
    ],
  );
  const editorOptions = useMemo(() => {
    return {
      editorProps: {
        handleDOMEvents: {
          dragstart: (view, event) => {
            if (!canDragAndDrop) {
              event.preventDefault();

              return true;
            }

            return false;
          },
        },
        handleKeyDown: (view, event) => {
          // If readonly, allow all events to pass through normally
          if (readonly || !canDragAndDrop) {
            return false;
          }

          const allowedKeys = [
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Home',
            'End',
            'PageUp',
            'PageDown',
          ];

          // Allow copy/paste/cut shortcuts
          // if (
          //   (event.ctrlKey || event.metaKey) &&
          //   ['c', 'v', 'x'].includes(event.key.toLowerCase())
          // ) {
          //   return false;
          // }

          // Block all other keypresses if not allowed key
          if (!allowedKeys.includes(event.key)) {
            event.preventDefault();

            return true;
          }

          return false;
        },
      },
    };
  }, [canDragAndDrop, readonly]);

  const FloatingToolbar = useMemo(() => {
    if (!floatingToolbarConfig.enabled) {
      return undefined;
    }

    return isCustomToolbarConfig(floatingToolbarConfig)
      ? floatingToolbarConfig.toolbar
      : (props) =>
          isInsertOnlyMode! ? null : (
            <DefaultFloatingToolbar
              editor={props.editor}
              config={{
                enableAskAiCommands:
                  floatingToolbarConfig.enableAskAiCommands ??
                  extensionConfig.askAiConfig.enabled,
                pageType: pageTypeProp,
              }}
            />
          );
  }, [
    floatingToolbarConfig,
    extensionConfig.askAiConfig.enabled,
    pageTypeProp,
    isInsertOnlyMode,
  ]);
  const toolbarConfig: EditorFloatingToolbarConfig = useMemo(() => {
    return FloatingToolbar == null || isDragging
      ? {
          enabled: false,
        }
      : {
          enabled: true,
          toolbar: FloatingToolbar,
        };
  }, [FloatingToolbar, isDragging]);

  return (
    <ErrorBoundary FallbackComponent={EditorErrorFallback}>
      <Editor
        $hasFloatingContentOutsideOfEditor={
          extensionConfig.commandPickerWithTriggerConfig.enabled
        }
        extensionConfig={extensionConfig}
        isInsertOnlyMode={isInsertOnlyMode}
        $disableGapCursor={!readonly || canDragAndDrop}
        $readonly={readonly}
        floatingToolbarConfig={toolbarConfig}
        hasTableToolbar={extensionConfig.tableToolbarConfig.enabled}
        className={className}
        placeholder={placeholder}
        editorOptions={editorOptions}
        extensions={extensions}
        value={value}
        onChange={onChange}
        onInitialize={onInitialize}
        readonly={readonly}
        isChatOpen={isChatOpen}
        documentTitle={documentTitle}
      />
    </ErrorBoundary>
  );
};
const StyledEditor = styled(RichTextEditorComponent)``;

export const DocumentEditor: typeof RichTextEditorComponent = StyledEditor;

function EditorErrorFallback() {
  return (
    <EditorFallbackWrapper>
      <EmptyState
        title={<FormattedMessage id="document-editor.error.fallback" />}
        image={<FontAwesomeIcon icon={faFileExclamation} size="2x" />}
      />
      <ReloadWrapper onClick={() => window.location.reload()}>
        <FormattedMessage id="document-editor.error.fallback.reload" />
      </ReloadWrapper>
    </EditorFallbackWrapper>
  );
}
